import React from "react";
import { motion } from "framer-motion";
import { Scissors, Award, Sparkles, Wrench, Eye, Flame, ArrowUpRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useBooking } from "../context/BookingContext";

const ICONS = [Scissors, Award, Sparkles, Wrench, Eye, Flame];

const WhyChoose = () => {
  const { t } = useLang();
  const { setOpen } = useBooking();

  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-24 md:py-36 bg-[#0a0a0a] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute -top-1/2 right-0 w-[60vw] h-[60vw] bg-gold/5 rounded-full blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <span className="h-px w-10 bg-gold" />
            <span className="label-eyebrow text-gold">{t.why.eyebrow}</span>
            <span className="h-px w-10 bg-gold" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display uppercase text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]"
          >
            {t.why.title}
          </motion.h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {t.why.items.map((item, i) => {
            const Icon = ICONS[i] || Scissors;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                data-testid={`why-item-${i}`}
                className="group relative bg-black/40 backdrop-blur border border-white/10 p-7 md:p-8 rounded-sm hover:border-gold/40 hover:bg-black/60 transition-all"
              >
                <div className="h-12 w-12 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center mb-5 group-hover:bg-gold group-hover:text-black transition-all">
                  <Icon className="h-5 w-5 text-gold group-hover:text-black transition-colors" strokeWidth={1.8} />
                </div>
                <div className="label-eyebrow text-white/40 mb-2">0{i + 1}</div>
                <h3 className="font-display text-xl md:text-2xl uppercase tracking-tight leading-tight">
                  {item}
                </h3>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-16 flex justify-center">
          <button
            onClick={() => setOpen(true)}
            data-testid="why-book-btn"
            className="group inline-flex items-center gap-2 bg-gold text-black px-8 py-4 rounded-sm font-display uppercase tracking-tight text-sm md:text-base hover:scale-[1.03] glow-gold-hover transition-all"
          >
            {t.nav.book}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
